import type { ReactNode } from 'react';
import { fmtMoney } from '@/lib/domain/format';
import { stageByApiName, stageLabel } from '@/lib/domain/stages';

type TagTone = 'default' | 'warn' | 'danger' | 'accent' | 'info';

const TAG_TONE: Record<TagTone, string> = {
  default: 'bg-vega-bg-2 text-vega-ink-2 border border-vega-line',
  accent: 'bg-vega-accent-soft text-vega-accent border border-transparent',
  warn: 'bg-vega-warn-soft text-vega-warn border border-transparent',
  danger: 'bg-vega-danger-soft text-vega-danger border border-transparent',
  info: 'bg-vega-info-soft text-vega-info border border-transparent',
};

export function Tag({
  children,
  tone = 'default',
  size = 'md',
}: {
  children: ReactNode;
  tone?: TagTone;
  size?: 'sm' | 'md';
}) {
  const pad = size === 'sm' ? 'px-1.5 py-px text-[10px]' : 'px-2 py-0.5 text-[11px]';
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-[3px] font-medium leading-tight tracking-[0.1px] ${pad} ${TAG_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

/** Valor em BRL compacto, monoespaçado. */
export function Money({ value }: { value: number | null | undefined }) {
  return (
    <span className="font-mono font-medium text-vega-ink" style={{ letterSpacing: -0.2 }}>
      {fmtMoney(value)}
    </span>
  );
}

/** Pílula da fase (texto em caixa alta, sem prefixo numérico). */
export function PhasePill({ stage, mini = false }: { stage: string; mini?: boolean }) {
  const def = stageByApiName(stage);
  const text = mini && def ? def.short : stageLabel(stage);
  return (
    <span className="inline-flex items-center font-mono text-[10px] font-medium uppercase tracking-wide text-vega-muted">
      {text}
    </span>
  );
}

/** Chip de aging (dias na fase atual), colorido pelo limite da fase. */
export function AgingChip({ days, threshold }: { days: number; threshold: number | null }) {
  let tone = 'text-vega-muted';
  if (threshold) {
    if (days > threshold) tone = 'text-vega-danger';
    else if (days > threshold * 0.7) tone = 'text-vega-warn';
  }
  return <span className={`inline-flex items-center font-mono text-[10px] font-medium ${tone}`}>{days}d</span>;
}
